import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Loader2, CheckCircle2, AlertTriangle, AlertCircle, ArrowLeft, RotateCcw } from "lucide-react";

function colorClasses(c) {
  if (c === 'green') return 'border-emerald-200 bg-emerald-50';
  if (c === 'yellow') return 'border-yellow-200 bg-yellow-50';
  if (c === 'red') return 'border-red-200 bg-red-50';
  return 'border-zinc-200 bg-white';
}
function colorIcon(c) {
  if (c === 'green') return <CheckCircle2 size={14} className="text-emerald-600" />;
  if (c === 'yellow') return <AlertTriangle size={14} className="text-yellow-600" />;
  if (c === 'red') return <AlertCircle size={14} className="text-red-600" />;
  return null;
}

export default function InterviewReview() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/interview/${id}`);
      setData(r.data);
    })();
  }, [id]);

  if (!data) return <div className="p-10"><Loader2 className="animate-spin" /></div>;
  if (data.status !== 'completed') return (
    <div className="p-10">
      <p>Interview not completed yet.</p>
      <Link to={`/app/interview/${id}`} className="text-[#0E7C66] font-semibold">Resume session</Link>
    </div>
  );

  const b = data.breakdown || {};
  const perQ = data.per_question || [];

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="interview-review">
      <Link to="/app/interview" className="text-sm text-zinc-500 inline-flex items-center gap-1 hover:text-black"><ArrowLeft size={14} /> Back to interviews</Link>

      <div className="mt-4 mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{data.level} · {data.role}</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Recruiter feedback</h1>
      </div>

      <div className="card-flat p-6 mb-6">
        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Overall</div>
            <div className="font-display font-black text-5xl mt-1" data-testid="overall-interview-score">{data.overall_score}<span className="text-zinc-400 text-xl">/100</span></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 flex-1 min-w-[280px]">
            <Stat label="Technical" value={b.technical_accuracy} />
            <Stat label="Communication" value={b.communication} />
            <Stat label="Confidence" value={b.confidence} />
            <Stat label="Relevance" value={b.relevance} />
            <Stat label="Problem Solving" value={b.problem_solving} />
          </div>
        </div>
        {data.recruiter_summary && (
          <p className="mt-5 text-sm text-zinc-700 leading-relaxed border-t border-zinc-200 pt-4">{data.recruiter_summary}</p>
        )}
        <div className="mt-4 flex gap-2">
          <Link to="/app/interview" className="px-4 h-10 rounded-md border border-zinc-300 text-sm font-semibold inline-flex items-center gap-1 hover:border-zinc-900"><RotateCcw size={14} /> Try another round</Link>
        </div>
      </div>

      {(data.improvement_areas || []).length > 0 && (
        <div className="card-flat p-6 mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Focus areas</div>
          <ul className="space-y-2">
            {data.improvement_areas.map((t,i)=>(<li key={i} className="text-sm flex items-start gap-2"><AlertTriangle size={14} className="text-yellow-600 mt-1 shrink-0" /> {t}</li>))}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        {data.questions.map((q, i) => {
          const pq = perQ.find(p => p.q_idx === i) || {};
          const answer = (data.answers.find(a => a.q_idx === i) || {}).text || "(no answer)";
          return (
            <div key={i} className={`rounded-xl border ${colorClasses(pq.color)} p-5`} data-testid={`review-q-${i}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Q{i+1} · {q.topic_category || q.category}{q.scenario ? ' · Scenario' : ''}{q.difficulty ? ` · ${q.difficulty}` : ''}</div>
                <div className="inline-flex items-center gap-2 text-xs font-semibold">
                  {colorIcon(pq.color)}
                  <span className="font-mono">Score {pq.score ?? '-'}/10</span>
                </div>
              </div>
              <div className="font-display font-bold mt-2">{q.question}</div>
              <div className="mt-3 grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Your answer</div>
                  <p className="leading-relaxed">{answer}</p>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Suggested ideal answer</div>
                  <p className="leading-relaxed">{pq.suggested_answer || q.ideal_answer || "—"}</p>
                </div>
              </div>
              {pq.feedback && (
                <div className="mt-3 pt-3 border-t border-black/5 text-sm leading-relaxed">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Recruiter feedback · </span>
                  {pq.feedback}
                </div>
              )}
              {(pq.missing_concepts || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {pq.missing_concepts.map((m,k)=>(<span key={k} className="tag tag-warn">{m}</span>))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-mono font-bold mt-1">{value || 0}</div>
      <div className="meter-track mt-1.5"><div className="meter-fill" style={{ width: `${value||0}%`, background:'#0E7C66' }} /></div>
    </div>
  );
}
